'use client';
import React, { useState } from 'react';
import { Bell, Shield, LogOut, ChevronRight, Globe, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

interface User {
  username: string;
  handle: string;
}

interface Props { user: User; }

export default function ProfilSettings({ user }: Props) {
  const router = useRouter();
  const { signOut } = useAuth();
  const [notifications, setNotifications] = useState({ likes: true, comments: true, events: true, newCafe: false });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      toast.success('Berhasil keluar. Sampai jumpa! 👋');
      router.push('/sign-up-login-screen');
      router.refresh();
    } catch (error: any) {
      toast.error(error?.message || 'Gagal keluar, coba lagi');
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="max-w-lg space-y-6">
      {/* Notifications */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-sm font-700 text-primary flex items-center gap-2">
            <Bell size={16} className="text-accent" />
            Notifikasi
          </h3>
        </div>
        {[
          { key: 'likes' as const, label: 'Like pada ulasanku', desc: 'Notif ketika ada yang like review kamu' },
          { key: 'comments' as const, label: 'Komentar baru', desc: 'Notif ketika ada yang comment' },
          { key: 'events' as const, label: 'Nongkrong Bareng', desc: 'Update event yang kamu ikuti' },
          { key: 'newCafe' as const, label: 'Cafe baru di Malang', desc: 'Notif ketika ada cafe baru ditambahkan' },
        ].map((item) => (
          <div key={`notif-${item.key}`} className="flex items-center justify-between px-5 py-4 border-b border-border last:border-0">
            <div>
              <p className="text-sm font-600 text-foreground">{item.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
            </div>
            <button
              onClick={() => setNotifications((prev) => ({ ...prev, [item.key]: !prev[item.key] }))}
              className={`relative w-11 h-6 rounded-full transition-colors ${notifications[item.key] ? 'bg-accent' : 'bg-muted'}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${notifications[item.key] ? 'translate-x-5.5 left-0.5' : 'left-0.5'}`} />
            </button>
          </div>
        ))}
      </div>

      {/* Privacy */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-sm font-700 text-primary flex items-center gap-2">
            <Shield size={16} className="text-accent" />
            Privasi & Keamanan
          </h3>
        </div>
        {[
          { label: 'Ubah Password', icon: Shield },
          { label: 'Bahasa Aplikasi', icon: Globe },
        ].map((item) => (
          <button key={`priv-${item.label}`} className="w-full flex items-center justify-between px-5 py-4 border-b border-border last:border-0 hover:bg-secondary transition-colors">
            <div className="flex items-center gap-3">
              <item.icon size={15} className="text-muted-foreground" />
              <span className="text-sm font-500 text-foreground">{item.label}</span>
            </div>
            <ChevronRight size={15} className="text-muted-foreground" />
          </button>
        ))}
      </div>

      {/* Danger Zone */}
      <div className="bg-card border border-red-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-red-100">
          <h3 className="text-sm font-700 text-red-600">Zona Bahaya</h3>
        </div>
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="w-full flex items-center gap-3 px-5 py-4 hover:bg-red-50 transition-colors"
        >
          <Trash2 size={15} className="text-red-500" />
          <span className="text-sm font-500 text-red-600">Hapus Akun Permanen</span>
        </button>
      </div>

      {/* Logout */}
      <button
        onClick={handleLogout}
        disabled={isLoggingOut}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl border border-border bg-card text-sm font-700 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
      >
        <LogOut size={16} />
        {isLoggingOut ? 'Keluar...' : 'Keluar dari Nongki'}
      </button>

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay animate-fade-in" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-5">
              <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Trash2 size={24} className="text-red-500" />
              </div>
              <h3 className="text-lg font-800 text-primary mb-2">Hapus Akun?</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Semua data kamu — ulasan, koleksi, dan riwayat — akan dihapus permanen. Tindakan ini tidak bisa dibatalkan.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="btn-secondary flex-1">Batal</button>
              <button
                onClick={() => { toast.error('Fitur hapus akun dalam pengembangan.'); setShowDeleteConfirm(false); }}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white text-sm font-700 hover:bg-red-600 transition-colors"
              >
                Hapus Akun
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}